import { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../api';
import { BRAND } from '../config/brand';
import EditPOForm from '../components/EditPOForm';

const G = BRAND.colors.primary;

// ─── PODetail ─────────────────────────────────────────────────────────────────
// View / edit a single PO. Drafts allow status + notes + minor edits;
// sent/confirmed are mostly read-only.
export default function PODetail({ poId, getToken, navigate, showToast }) {
  const [po, setPo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [notes, setNotes] = useState('');
  const [externalNotes, setExternalNotes] = useState('');
  // Edit mode (June 2026). When true, render EditPOForm in place of the read-only
  // view. Drafts edit everything; non-drafts edit a subset (server-enforced).
  // Toggled by the Edit button in the top actions bar.
  const [editMode, setEditMode] = useState(false);
  // Whether the distributor has connected an email account (Settings →
  // Notifications) — the "Email to supplier" button is disabled without one,
  // since without it the send has nothing to actually send through. null =
  // still checking, true/false = known.
  const [emailConnected, setEmailConnected] = useState(null);
  // Compose modal state — editable to/cc/subject/body, pre-populated from
  // the server's draft endpoint when opened.
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailModalLoading, setEmailModalLoading] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailCc, setEmailCc] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [emailAttachments, setEmailAttachments] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const data = await api.withToken(token).getPO(poId);
      setPo(data);
      setNotes(data.notes || '');
      setExternalNotes(data.external_notes || '');
    } catch (e) {
      showToast?.(e.message || 'Failed to load PO', 'error');
    } finally {
      setLoading(false);
    }
  }, [poId, getToken, showToast]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const status = await api.withToken(token).getEmailCredentials();
        setEmailConnected(!!status.connected);
      } catch {
        setEmailConnected(false);
      }
    })();
  }, [getToken]);

  const updateStatus = async (newStatus) => {
    setSaving(true);
    try {
      const token = await getToken();
      const updated = await api.withToken(token).updatePO(poId, { status: newStatus });
      setPo(updated);
      showToast?.(`Marked as ${newStatus}`, 'success');
    } catch (e) {
      showToast?.(e.message || 'Update failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const saveNotes = async () => {
    setSaving(true);
    try {
      const token = await getToken();
      const updated = await api.withToken(token).updatePO(poId, { notes });
      setPo(updated);
      showToast?.('Notes saved', 'success');
    } catch (e) {
      showToast?.(e.message || 'Save failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const saveExternalNotes = async () => {
    setSaving(true);
    try {
      const token = await getToken();
      const updated = await api.withToken(token).updatePO(poId, { external_notes: externalNotes });
      setPo(updated);
      showToast?.('Supplier notes saved', 'success');
    } catch (e) {
      showToast?.(e.message || 'Save failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const downloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const token = await getToken();
      const blob = await api.withToken(token).downloadPOPdf(poId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${po.po_number}-${po.supplier_name}.pdf`.replace(/[^\x20-\x7E]/g, '');
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      showToast?.(e.message || 'PDF download failed', 'error');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const openEmailModal = async () => {
    if (emailConnected !== true) {
      showToast?.('Connect an email account in Settings → Notifications first.', 'error');
      return;
    }
    setEmailModalLoading(true);
    setEmailModalOpen(true);
    try {
      const token = await getToken();
      const draft = await api.withToken(token).getPOEmailDraft(poId);
      setEmailTo(draft.to || '');
      setEmailCc('');
      setEmailSubject(draft.subject || '');
      setEmailBody(draft.body || '');
      setEmailAttachments(draft.attachments || []);
    } catch (e) {
      showToast?.(e.message || 'Could not load email draft', 'error');
      setEmailModalOpen(false);
    } finally {
      setEmailModalLoading(false);
    }
  };

  const confirmSendEmail = async () => {
    if (!emailTo.trim()) {
      showToast?.('Add a recipient email first.', 'error');
      return;
    }
    setSendingEmail(true);
    try {
      const token = await getToken();
      const result = await api.withToken(token).emailPO(poId, {
        to: emailTo.trim(), cc: emailCc.trim(), subject: emailSubject.trim(), body: emailBody,
      });
      const artNote = result.missing_art_count > 0
        ? ` (${result.missing_art_count} artwork file${result.missing_art_count === 1 ? '' : 's'} couldn't be attached — follow up separately)`
        : (result.attached_art_count > 0 ? ` with ${result.attached_art_count} artwork file${result.attached_art_count === 1 ? '' : 's'} attached` : '');
      showToast?.(`Sent to supplier${artNote}.`, 'success');
      setEmailModalOpen(false);
    } catch (e) {
      showToast?.(e.message || 'Send failed', 'error');
    } finally {
      setSendingEmail(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete ${po.po_number}? This cannot be undone.`)) return;
    setSaving(true);
    try {
      const token = await getToken();
      await api.withToken(token).deletePO(poId);
      showToast?.('PO deleted', 'success');
      navigate?.('pos');
    } catch (e) {
      showToast?.(e.message || 'Delete failed', 'error');
      setSaving(false);
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Loading…</div>;
  if (!po) return <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>PO not found.</div>;

  // Edit mode swaps the whole view. EditPOForm calls onSaved with the updated
  // row, which we use to refresh local state AND exit edit mode in one step.
  if (editMode) {
    return (
      <EditPOForm
        po={po}
        getToken={getToken}
        showToast={showToast}
        onCancel={() => setEditMode(false)}
        onSaved={(updated) => { setPo(updated); setNotes(updated.notes || ''); setExternalNotes(updated.external_notes || ''); setEditMode(false); }}
      />
    );
  }

  const isDraft = po.status === 'draft';
  const items = Array.isArray(po.items) ? po.items : [];
  const decoItems = Array.isArray(po.deco_items) ? po.deco_items : [];
  const isDecorator = po.kind === 'decorator';
  const charges = Array.isArray(po.charges) ? po.charges : [];
  const shipTo = po.ship_to || {};
  const supplierContact = po.supplier_contact || {};
  const artworkFiles = collectArtworkFiles(po);

  return (
    <div style={{ maxWidth: 920, margin: '0 auto', padding: 24 }}>
      {/* Top actions bar */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 24, gap: 12 }}>
        <button onClick={() => navigate?.('pos')} style={btnLink}>← Back to POs</button>
        <div style={{ flex: 1 }} />
        <button onClick={() => setEditMode(true)} style={btnSecondary}>Edit</button>
        <button onClick={downloadPdf} disabled={downloadingPdf} style={btnSecondary}>
          {downloadingPdf ? 'Generating…' : 'Download PDF'}
        </button>
        <button
          onClick={openEmailModal}
          disabled={sendingEmail || emailConnected !== true}
          title={emailConnected === false ? 'Connect an email account in Settings → Notifications first' : undefined}
          style={{ ...btnSecondary, opacity: emailConnected === false ? 0.5 : 1, cursor: emailConnected === false ? 'not-allowed' : 'pointer' }}>
          {sendingEmail ? 'Sending…' : 'Email to supplier'}
        </button>
      </div>

      {/* No email account connected — this is the whole reason the button
          above is disabled, so say exactly where to fix that rather than
          leaving a disabled button with no explanation. */}
      {emailConnected === false && (
        <div style={{
          background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 8,
          padding: '12px 16px', marginBottom: 16, fontSize: 13, color: '#1E3A8A',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        }}>
          <div>
            <strong>Emailing POs is disabled until you connect an email account.</strong>{' '}
            Go to <strong>Settings → Notifications</strong> and connect your SendGrid API key —
            takes about a minute, and you can send yourself a test email there to confirm it's working
            before relying on it.
          </div>
          <button onClick={() => navigate?.('settings')} style={{ ...btnSecondary, whiteSpace: 'nowrap', flexShrink: 0 }}>
            Open Settings
          </button>
        </div>
      )}

      {/* Artwork reminder — only relevant if sending manually via Download PDF;
          "Email to supplier" now attaches these files automatically. */}
      {artworkFiles.length > 0 && (
        <div style={{
          background: '#fefce8', border: '1px solid #fde047', borderRadius: 8,
          padding: '12px 16px', marginBottom: 16, fontSize: 13, color: '#713f12',
        }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>
            ⚠️ Sending this manually (e.g. Download PDF + your own email)? Don't forget these files —
            "Email to supplier" attaches them automatically:
          </div>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {artworkFiles.map((f, i) => <li key={i} style={{ marginBottom: 2 }}>{f}</li>)}
          </ul>
        </div>
      )}

      {/* Header card */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', color: '#64748b', letterSpacing: 0.5 }}>
              Purchase Order
            </div>
            <div style={{ fontSize: 28, fontWeight: 700, marginTop: 4 }}>{po.po_number}</div>
            <div style={{ fontSize: 13, color: '#64748b', marginTop: 6 }}>
              Created {new Date(po.created_at).toLocaleDateString()}
              {po.client_name && ` · For ${po.client_name}`}
            </div>
          </div>
          <StatusControl status={po.status} onChange={updateStatus} disabled={saving} />
        </div>
      </div>

      {/* Parties */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 16 }}>
        <div style={cardStyle}>
          <SectionLabel>Vendor</SectionLabel>
          <div style={{ fontSize: 15, fontWeight: 600, marginTop: 4 }}>{po.supplier_name}</div>
          {supplierContact.phone && <div style={mutedLine}>{supplierContact.phone}</div>}
          {supplierContact.email && <div style={mutedLine}>{supplierContact.email}</div>}
          {po.account_number && (
            <div style={{ ...mutedLine, marginTop: 8 }}>Distributor account: <strong>{po.account_number}</strong></div>
          )}
        </div>
        <div style={cardStyle}>
          <SectionLabel>Ship to</SectionLabel>
          <div style={{ fontSize: 15, fontWeight: 600, marginTop: 4 }}>
            {shipTo.name || po.client_name || '—'}
          </div>
          {shipTo.company && <div style={mutedLine}>{shipTo.company}</div>}
          {shipTo.address1 && <div style={mutedLine}>{shipTo.address1}</div>}
          {shipTo.address2 && <div style={mutedLine}>{shipTo.address2}</div>}
          {(shipTo.city || shipTo.state || shipTo.zip) && (
            <div style={mutedLine}>
              {[shipTo.city, shipTo.state, shipTo.zip].filter(Boolean).join(', ').replace(/, (\w{2}), /, ', $1 ')}
            </div>
          )}
          {shipTo.phone && <div style={mutedLine}>{shipTo.phone}</div>}
        </div>
      </div>

      {/* Order details */}
      <div style={{ ...cardStyle, marginTop: 16 }}>
        <SectionLabel>Order details</SectionLabel>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 24, marginTop: 8 }}>
          <KV label="In-hand by" value={po.in_hand_date ? new Date(po.in_hand_date).toLocaleDateString() : '—'} />
          <KV label="Terms" value={po.terms || '—'} />
          <KV label="Shipping" value={po.shipping_method || '—'} />
          <KV label="Proof" value={po.proof_required === false ? 'Not required' : 'Required'} />
        </div>
      </div>

      {/* Items — decoration breakdown for a decorator PO, else product items */}
      <div style={{ ...cardStyle, marginTop: 16 }}>
        {isDecorator ? (
          <>
            <SectionLabel>Decoration to run</SectionLabel>
            <div style={{ marginTop: 12 }}>
              {decoItems.map((d, i) => <DecoJobDisplay key={i} d={d} />)}
            </div>
          </>
        ) : (
          <>
            <SectionLabel>Items ({items.length})</SectionLabel>
            <div style={{ marginTop: 12 }}>
              {items.map((item, i) => <ItemDisplay key={i} item={item} />)}
            </div>
          </>
        )}
        {/* Totals */}
        <div style={{
          paddingTop: 14, marginTop: 8, borderTop: '1px solid #e2e8f0',
          display: 'flex', justifyContent: 'flex-end', fontSize: 13,
        }}>
          <div style={{ width: 240 }}>
            <Totals subtotal={po.subtotal} charges={charges} total={po.total} />
          </div>
        </div>
      </div>

      {/* Supporting documents — proofs, spec sheets, reference files general
          to the whole PO, not tied to one location/art. */}
      {Array.isArray(po.supporting_files) && po.supporting_files.length > 0 && (
        <div style={{ ...cardStyle, marginTop: 16 }}>
          <SectionLabel>Supporting documents</SectionLabel>
          <div style={{ marginTop: 6 }}>
            {po.supporting_files.map((f, idx) => (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                {f.art_file_mime && f.art_file_mime.startsWith('image/') ? (
                  <a href={f.art_file_url} target="_blank" rel="noreferrer">
                    <img src={f.art_file_url} alt={f.art_file_name || 'File'}
                      style={{ width: 28, height: 28, borderRadius: 4, objectFit: 'contain', border: '1px solid #E2E8F0', background: '#fff', padding: 1 }} />
                  </a>
                ) : null}
                <a href={f.art_file_url} target="_blank" rel="noreferrer" style={{ fontSize: 13, color: G, textDecoration: 'none' }}>
                  📎 {f.art_file_name || 'file'}
                </a>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Legacy decoration_notes for old POs without per-item decorations */}
      {po.decoration_notes && !items.some(it => Array.isArray(it.decorations) && it.decorations.length > 0) && (
        <div style={{ ...cardStyle, marginTop: 16 }}>
          <SectionLabel>Decoration / imprint instructions</SectionLabel>
          <div style={{ fontSize: 13, marginTop: 6, whiteSpace: 'pre-wrap' }}>{po.decoration_notes}</div>
        </div>
      )}

      {/* Notes to supplier (prints on PO) */}
      <div style={{ ...cardStyle, marginTop: 16 }}>
        <SectionLabel>Notes to supplier (prints on PO)</SectionLabel>
        <textarea
          value={externalNotes}
          onChange={e => setExternalNotes(e.target.value)}
          rows={3}
          placeholder="Visible to the supplier on the PO…"
          style={{
            width: '100%', marginTop: 6, padding: '10px 12px',
            border: '1px solid #cbd5e1', borderRadius: 6,
            fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box', resize: 'vertical',
          }}
        />
        {externalNotes !== (po.external_notes || '') && (
          <div style={{ marginTop: 8, textAlign: 'right' }}>
            <button onClick={saveExternalNotes} disabled={saving} style={btnPrimary}>
              {saving ? 'Saving…' : 'Save supplier notes'}
            </button>
          </div>
        )}
      </div>

      {/* Internal notes */}
      <div style={{ ...cardStyle, marginTop: 16 }}>
        <SectionLabel>Internal notes</SectionLabel>
        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          rows={3}
          placeholder="Notes for your team (not printed on PO)…"
          style={{
            width: '100%', marginTop: 6, padding: '10px 12px',
            border: '1px solid #cbd5e1', borderRadius: 6,
            fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box', resize: 'vertical',
          }}
        />
        {notes !== (po.notes || '') && (
          <div style={{ marginTop: 8, textAlign: 'right' }}>
            <button onClick={saveNotes} disabled={saving} style={btnPrimary}>
              {saving ? 'Saving…' : 'Save notes'}
            </button>
          </div>
        )}
      </div>

      {/* Danger zone */}
      <div style={{ marginTop: 32, paddingTop: 16, borderTop: '1px solid #fee2e2' }}>
        <button onClick={remove} disabled={saving} style={{
          ...btnSecondary, color: '#dc2626', borderColor: '#fca5a5',
        }}>
          Delete this PO
        </button>
      </div>

      {emailModalOpen && (
        <EmailComposeModal
          loading={emailModalLoading}
          sending={sendingEmail}
          to={emailTo} setTo={setEmailTo}
          cc={emailCc} setCc={setEmailCc}
          subject={emailSubject} setSubject={setEmailSubject}
          body={emailBody} setBody={setEmailBody}
          attachments={emailAttachments}
          onSend={confirmSendEmail}
          onClose={() => setEmailModalOpen(false)}
        />
      )}
    </div>
  );
}

// ─── EmailComposeModal: review/edit the PO email before it actually sends ────
// Pre-populated from the server's draft endpoint (same subject/body the old
// auto-send used) but every field is editable — recipient, cc, subject, and
// body — before the real send fires. Attachments are shown but not editable
// here (they're generated/fetched server-side at send time); this is about
// verifying who it's going to and what it says, not managing the files.
function EmailComposeModal({ loading, sending, to, setTo, cc, setCc, subject, setSubject, body, setBody, attachments, onSend, onClose }) {
  const fieldLabel = { fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4, display: 'block' };
  const fieldInput = {
    width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6,
    fontSize: 13, fontFamily: 'inherit', outline: 'none', color: '#0f172a', boxSizing: 'border-box',
  };
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }} onClick={onClose}>
      <div style={{
        background: '#fff', borderRadius: 10, width: '100%', maxWidth: 560, maxHeight: '90vh',
        display: 'flex', flexDirection: 'column', boxShadow: '0 20px 50px rgba(0,0,0,0.2)',
      }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>Email to supplier</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 18, color: '#94a3b8', cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>Loading draft…</div>
        ) : (
          <>
            <div style={{ padding: 20, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <label style={fieldLabel}>To</label>
                <input value={to} onChange={e => setTo(e.target.value)} placeholder="supplier@example.com" style={fieldInput} />
              </div>
              <div>
                <label style={fieldLabel}>Cc <span style={{ fontWeight: 400, textTransform: 'none', color: '#cbd5e1' }}>(optional)</span></label>
                <input value={cc} onChange={e => setCc(e.target.value)} placeholder="you@yourcompany.com" style={fieldInput} />
              </div>
              <div>
                <label style={fieldLabel}>Subject</label>
                <input value={subject} onChange={e => setSubject(e.target.value)} style={fieldInput} />
              </div>
              <div>
                <label style={fieldLabel}>Message</label>
                <textarea value={body} onChange={e => setBody(e.target.value)} rows={8}
                  style={{ ...fieldInput, resize: 'vertical', lineHeight: 1.5 }} />
              </div>
              {attachments.length > 0 && (
                <div>
                  <label style={fieldLabel}>Attachments</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {attachments.map((f, i) => (
                      <span key={i} style={{
                        fontSize: 12, color: '#334155', background: '#f1f5f9', borderRadius: 5,
                        padding: '4px 9px', display: 'inline-flex', alignItems: 'center', gap: 4,
                      }}>📎 {f}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div style={{ padding: '14px 20px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={onClose} disabled={sending} style={btnSecondary}>Cancel</button>
              <button onClick={onSend} disabled={sending || !to.trim()} style={{ ...btnPrimary, opacity: (sending || !to.trim()) ? 0.6 : 1 }}>
                {sending ? 'Sending…' : 'Send'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── DecoJobDisplay: one imprint on a saved decorator PO ─────────────────────
// Groups garments Location → Art → Ink with the size/qty breakdown so the
// decorator knows exactly what to expect and check in.
function groupByArtInk(garments) {
  const byArt = [];
  for (const g of garments || []) {
    const artKey = g.art || 'Art';
    let a = byArt.find(x => x.art === artKey);
    if (!a) { a = { art: artKey, inkGroups: [] }; byArt.push(a); }
    const inkKey = g.ink || '';
    let ig = a.inkGroups.find(x => x.ink === inkKey);
    if (!ig) { ig = { ink: inkKey, garments: [] }; a.inkGroups.push(ig); }
    ig.garments.push(g);
  }
  byArt.forEach(a => a.inkGroups.forEach((ig, i) => { ig.isColorChange = i > 0; }));
  return byArt;
}

function DecoJobDisplay({ d }) {
  const lbl = { fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' };
  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 12, marginBottom: 10, background: '#f8fafc' }}>
      <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a', marginBottom: 4 }}>{d.spec}</div>
      <div style={{ fontSize: 12, color: '#475569', marginBottom: 8 }}>
        {d.multi_location ? `${d.impressions} prints (${d.combined_qty} pcs × ${(d.locations||[]).length||2} locations)` : `${d.combined_qty} pcs`} · {d.run_rate != null ? `$${Number(d.run_rate).toFixed(2)}/${d.multi_location ? 'print' : 'pc'}` : '—'} = <strong>${Number(d.run_total || 0).toFixed(2)}</strong>
        {d.setup_total > 0 ? <> · setup <strong>${Number(d.setup_total).toFixed(2)}</strong></> : null}
        {(d.art_count || d.color_changes) ? <span style={{ color: '#94a3b8' }}> · {d.art_count || 1} art{(d.art_count || 1) === 1 ? '' : 's'}{d.color_changes ? `, ${d.color_changes} color change${d.color_changes === 1 ? '' : 's'}` : ''}</span> : null}
        {/* Per-job total. Setup used to appear only as one pooled "Setup
            charges" line at the bottom of the PO, which made it impossible for
            the decorator to see what any single imprint was worth. Totalling
            per job here is what lets that bottom line go away. */}
        <> · total deco cost = <strong style={{ color: '#0f172a' }}>${(Number(d.run_total || 0) + Number(d.setup_total || 0)).toFixed(2)}</strong></>
      </div>
      {d.print_colors ? (
        <div style={{ fontSize: 12, marginBottom: 8, padding: '6px 10px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6 }}>
          <span style={lbl}>Print colors: </span><strong>{d.print_colors}</strong>
        </div>
      ) : null}
      {Array.isArray(d.locations) && d.locations.length > 1 ? (
        <div style={{ fontSize: 12, marginBottom: 8, padding: '6px 10px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6 }}>
          <span style={lbl}>Artwork printed on: </span><strong>{d.locations.join(', ')}</strong> <span style={{ color: '#94a3b8' }}>· same artwork each location</span>
        </div>
      ) : null}
      <div style={{ ...lbl, marginBottom: 4 }}>Garments to decorate (check in on arrival)</div>
      {groupByArtInk(d.garments).map((a, ai) => {
        const artFile = a.inkGroups.flatMap(ig => ig.garments).find(g => g.art_file_id || g.art_file_url);
        return (
        <div key={ai} style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span>{a.art}<span style={{ fontSize: 10, fontWeight: 400, color: '#94a3b8' }}> · setup</span></span>
            {artFile && artFile.art_file_mime && artFile.art_file_mime.startsWith('image/') && (
              <a href={artFile.art_file_url} target="_blank" rel="noreferrer">
                <img src={artFile.art_file_url} alt={artFile.art_file_name || 'Art'}
                  style={{ width: 24, height: 24, borderRadius: 4, objectFit: 'contain', border: '1px solid #E2E8F0', background: '#fff', padding: 1 }} />
              </a>
            )}
            {artFile && (
              <a href={artFile.art_file_url} target="_blank" rel="noreferrer"
                style={{ fontSize: 11, fontWeight: 500, color: G, textDecoration: 'none' }}>
                📎 {artFile.art_file_name || 'art file'}
              </a>
            )}
          </div>
          {a.inkGroups.map((ig, ii) => (
            <div key={ii}>
              {ig.isColorChange && (
                <div style={{ fontSize: 10, color: '#b45309', fontWeight: 600, margin: '3px 0', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ flex: 1, borderTop: '1px dashed #fcd34d' }} /> color change <span style={{ flex: 1, borderTop: '1px dashed #fcd34d' }} />
                </div>
              )}
              {ig.ink ? <div style={{ fontSize: 11, color: '#475569', fontWeight: 500, paddingLeft: 8, marginBottom: 2 }}>{ig.ink} ink</div> : null}
              {ig.garments.map((g, gi) => (
                <div key={gi} style={{ fontSize: 12, color: '#334155', paddingLeft: 8, marginBottom: 2 }}>
                  {g.item_no != null ? <strong>#{g.item_no} </strong> : null}{g.name}{g.color ? ` · ${g.color}` : ''} — {g.qty} pcs
                  {Array.isArray(g.sizes) && g.sizes.length ? <span style={{ color: '#94a3b8' }}> ({g.sizes.map(s => `${s.label}:${s.qty}`).join(', ')})</span> : null}
                </div>
              ))}
            </div>
          ))}
        </div>
        );
      })}
    </div>
  );
}

// ─── ItemDisplay: renders a single item with its decorations ────────────────
function ItemDisplay({ item }) {
  const decorations = Array.isArray(item.decorations) ? item.decorations : [];
  return (
    <div style={{
      border: '1px solid #e2e8f0', borderRadius: 8, marginBottom: 10, overflow: 'hidden',
    }}>
      {/* Header row */}
      <div style={{
        display: 'grid', gridTemplateColumns: '1fr 80px 90px 100px',
        padding: '12px 16px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0',
        alignItems: 'center', gap: 8,
      }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: 14 }}>{item.name}</div>
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
            {[item.sku && `SKU: ${item.sku}`, item.color].filter(Boolean).join(' · ')}
          </div>
        </div>
        <div style={{ textAlign: 'right', fontSize: 13 }}>{item.qty}</div>
        <div style={{ textAlign: 'right', fontSize: 13 }}>${Number(item.unit_cost).toFixed(2)}</div>
        <div style={{ textAlign: 'right', fontSize: 14, fontWeight: 700, color: G }}>${Number(item.line_total).toFixed(2)}</div>
      </div>

      {/* Body: mockup file + decorations */}
      <div style={{ padding: '12px 16px' }}>
        {item.mockup_filename && (
          <div style={{ fontSize: 12, marginBottom: 10 }}>
            <span style={{ color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, fontSize: 11 }}>Mockup file: </span>
            <span style={{ color: '#334155' }}>{item.mockup_filename}</span>
          </div>
        )}

        {decorations.length === 0 ? (
          <div style={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic' }}>
            No decoration specified for this item.
          </div>
        ) : (
          <>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 }}>
              Decoration ({decorations.length} location{decorations.length === 1 ? '' : 's'})
            </div>
            {decorations.map((d, i) => <DecoDisplay key={i} deco={d} />)}
          </>
        )}
      </div>
    </div>
  );
}

function DecoDisplay({ deco }) {
  return (
    <div style={{
      border: '1px solid #e2e8f0', borderRadius: 6, padding: '10px 12px',
      marginBottom: 6, background: '#fafbfc',
    }}>
      <div style={{ fontWeight: 600, fontSize: 13, color: '#0f172a', marginBottom: 6 }}>
        {deco.location || 'Location'}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px', fontSize: 12 }}>
        {deco.method && <Pair label="Method" value={deco.method} />}
        {deco.colors && <Pair label="Colors" value={deco.colors} />}
        {deco.size && <Pair label="Size" value={deco.size} />}
        {deco.art_file_id ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, fontSize: 10 }}>Artwork file: </span>
            {deco.art_file_mime && deco.art_file_mime.startsWith('image/') ? (
              <a href={deco.art_file_url} target="_blank" rel="noreferrer">
                <img src={deco.art_file_url} alt={deco.art_file_name || 'Art'}
                  style={{ width: 24, height: 24, borderRadius: 4, objectFit: 'contain', border: '1px solid #E2E8F0', background: '#fff', padding: 1 }} />
              </a>
            ) : null}
            <a href={deco.art_file_url} target="_blank" rel="noreferrer" style={{ color: G, textDecoration: 'none' }}>
              📎 {deco.art_file_name || deco.artwork_filename || 'art file'}
            </a>
          </div>
        ) : (
          deco.artwork_filename && <Pair label="Artwork file" value={deco.artwork_filename} />
        )}
      </div>
      {deco.notes && (
        <div style={{ fontSize: 12, color: '#475569', fontStyle: 'italic', marginTop: 6, lineHeight: 1.4 }}>
          {deco.notes}
        </div>
      )}
    </div>
  );
}

function Pair({ label, value }) {
  return (
    <div>
      <span style={{ color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, fontSize: 10 }}>{label}: </span>
      <span style={{ color: '#334155' }}>{value}</span>
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
// Walk all items + their decorations and collect every artwork/mockup filename.
function collectArtworkFiles(po) {
  const items = Array.isArray(po?.items) ? po.items : [];
  const set = new Set();
  for (const item of items) {
    if (item.mockup_filename) set.add(item.mockup_filename);
    const decos = Array.isArray(item.decorations) ? item.decorations : [];
    for (const d of decos) {
      if (d?.artwork_filename) set.add(d.artwork_filename);
    }
  }
  return [...set].sort();
}

function StatusControl({ status, onChange, disabled }) {
  const transitions = {
    draft:     [{ to: 'sent',      label: 'Mark as sent' }, { to: 'cancelled', label: 'Cancel' }],
    sent:      [{ to: 'confirmed', label: 'Mark confirmed' }, { to: 'cancelled', label: 'Cancel' }],
    confirmed: [],
    cancelled: [{ to: 'draft', label: 'Reopen as draft' }],
  };
  const opts = transitions[status] || [];
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
      <StatusBadge status={status} />
      {opts.map(o => (
        <button
          key={o.to}
          onClick={() => onChange(o.to)}
          disabled={disabled}
          style={{ ...btnSecondary, fontSize: 12, padding: '6px 12px' }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function StatusBadge({ status }) {
  const styles = {
    draft:     { bg: '#f1f5f9', fg: '#475569' },
    sent:      { bg: '#dbeafe', fg: '#1e40af' },
    confirmed: { bg: '#dcfce7', fg: '#166534' },
    cancelled: { bg: '#fee2e2', fg: '#991b1b' },
  };
  const s = styles[status] || styles.draft;
  return (
    <span style={{
      background: s.bg, color: s.fg, padding: '4px 12px',
      borderRadius: 12, fontSize: 11, fontWeight: 600,
      textTransform: 'capitalize',
    }}>{status}</span>
  );
}

function Totals({ subtotal, charges, total }) {
  return (
    <>
      <Row label="Subtotal" value={`$${Number(subtotal).toFixed(2)}`} />
      {(charges || []).map((c, i) => (
        c?.label ? <Row key={i} label={c.label} value={`$${Number(c.amount || 0).toFixed(2)}`} /> : null
      ))}
      <div style={{ height: 1, background: '#e2e8f0', margin: '8px 0' }} />
      <Row label="Total" value={`$${Number(total).toFixed(2)}`} bold />
    </>
  );
}
function Row({ label, value, bold }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between',
      fontWeight: bold ? 700 : 400, fontSize: bold ? 14 : 13,
      padding: '2px 0',
    }}>
      <span style={{ color: bold ? '#0f172a' : '#64748b' }}>{label}</span>
      <span>{value}</span>
    </div>
  );
}
function KV({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color: '#94a3b8', letterSpacing: 0.5 }}>
        {label}
      </div>
      <div style={{ marginTop: 4, fontSize: 14 }}>{value}</div>
    </div>
  );
}
function SectionLabel({ children }) {
  return (
    <div style={{
      fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
      color: '#64748b', letterSpacing: 0.5,
    }}>{children}</div>
  );
}

const cardStyle = {
  background: '#fff', border: '1px solid #e2e8f0',
  borderRadius: 8, padding: 20,
};
const mutedLine = { fontSize: 13, color: '#64748b', marginTop: 2 };
const btnPrimary = {
  padding: '8px 16px', background: G, color: '#fff', border: 'none',
  borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer',
};
const btnSecondary = {
  padding: '8px 16px', background: '#fff', color: '#334155',
  border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13,
  fontWeight: 600, cursor: 'pointer',
};
const btnLink = {
  background: 'none', border: 'none', color: '#64748b',
  fontSize: 13, cursor: 'pointer', padding: 4, fontWeight: 500,
};
